export type ClusterKey = 'root' | 'catalog' | 'pricing' | 'customers' | 'crm' | 'billing';

export interface Entity {
  id: string;
  name: string;
  cluster: ClusterKey;
  oneLiner: string;
  plain: string;
  fields: [string, string][];
  example: {
    title: string;
    lines: [string, string][];
  };
  connects: string[];
}

export const ENTITIES: Record<string, Entity> = {
  tenant: {
    id: 'tenant',
    name: 'Tenant',
    cluster: 'root',
    oneLiner: 'Your company — the seller running its subscription business on Revvly.',
    plain:
      "This is your company's workspace in Revvly. Every catalog item, customer, subscription, invoice, and pricing decision you create is scoped to your Tenant. Other tenants are fully isolated — your subscription revenue data stays private.",
    fields: [
      ['Name', 'Your company name'],
      ['Reporting currency', 'The single currency all analytics roll up to (e.g. USD)'],
      ['Invoice defaults', 'Numbering, payment terms, tax setup'],
      [
        'Payment information',
        'Banking details per region and currency, plus free-form payment instructions, rendered on invoice PDFs',
      ],
      ['Analytics config', 'Churn grace period, reactivation window'],
    ],
    example: {
      title: 'Acme SaaS',
      lines: [
        ['Reporting currency', 'USD'],
        ['Invoice prefix', 'ACME-'],
        ['Churn grace', '14 days'],
      ],
    },
    connects: ['Contains everything else'],
  },

  region: {
    id: 'region',
    name: 'Region',
    cluster: 'catalog',
    oneLiner: 'A geographic market you sell into, with its own currency.',
    plain:
      "Think of a Region as a market — United States, Europe, Brazil. Each one has a default currency and a list of supported currencies. Clients are assigned to a Region, which sets their default billing currency. Variable Component Prices can also target a region indirectly, through the bundle or module they're scoped to.",
    fields: [
      ['Code', 'Short identifier (e.g. "US", "EU")'],
      ['Name', 'Display name (e.g. "United States")'],
      ['Default currency', 'Primary currency for this market'],
      ['Supported currencies', 'Other currencies customers can pay in'],
    ],
    example: {
      title: 'Region: US',
      lines: [
        ['Default', 'USD'],
        ['Also accepts', 'CAD, MXN'],
      ],
    },
    connects: [
      'Each Client belongs to one Region',
      'Drives the default currency for new Subscriptions',
    ],
  },

  featureFlag: {
    id: 'featureFlag',
    name: 'Feature Flag',
    cluster: 'catalog',
    oneLiner: "The smallest switch — a single capability that's on or off.",
    plain:
      "A Feature Flag is one tiny thing a customer either has or doesn't have — 'advanced reporting', 'API access', 'custom branding'. Flags are grouped into Module tiers and Bundles; you can also override them per customer with an OrganizationFeatureFlag.",
    fields: [
      ['Key', 'Machine-readable id (e.g. "api-access")'],
      ['Name', 'Human-friendly label'],
      ['Category', 'Optional grouping for display'],
      ['Active', 'Whether the flag is currently available'],
    ],
    example: {
      title: 'advanced-reporting',
      lines: [
        ['Category', 'Analytics'],
        ['Status', 'Active'],
      ],
    },
    connects: [
      'Grouped into Module tiers',
      'Can be added directly to a Bundle',
      'Can be overridden per-customer (enable/disable)',
    ],
  },

  module: {
    id: 'module',
    name: 'Module',
    cluster: 'catalog',
    oneLiner: 'A versioned bundle of Feature Flags and Variable Components.',
    plain:
      "A Module is a sellable product capability — 'Analytics Module', 'Support Module'. It groups Feature Flags (organised into tiers like Basic/Pro) with the Variable Components that meter that capability — seats, storage, API calls. Modules are immutable once Published: editing them creates a new version, leaving existing subscriptions on the version they were sold.",
    fields: [
      ['Name', 'e.g. "Analytics Module"'],
      ['Version', 'Auto-incremented on each Publish'],
      ['Status', 'Draft / Published / Archived'],
      ['Tiers', 'Each tier exposes its own Feature Flags'],
      ['Variable Component refs', 'Pinned to specific component versions'],
      ['Available periods', 'Monthly / Quarterly / Yearly'],
    ],
    example: {
      title: 'Analytics Module · v3',
      lines: [
        ['Tiers', 'Basic, Pro'],
        ['Components', 'Seats (v2), API calls (v1)'],
        ['Status', 'Published'],
      ],
    },
    connects: [
      'Included in Bundles',
      'Subscriptions pin a specific Module version',
      'Holds versioned refs to Variable Components',
    ],
  },

  bundle: {
    id: 'bundle',
    name: 'Bundle',
    cluster: 'catalog',
    oneLiner: 'A versioned package of Modules sold at a single tier — Starter, Pro, Enterprise.',
    plain:
      "A Bundle is the plan your customers buy — 'Starter', 'Professional', 'Enterprise'. It pins specific Module versions, may include extra Variable Components directly, and carries bundle-level feature flags. Like Modules, Bundles are versioned: each Publish creates an immutable new version that subscriptions can pin to.",
    fields: [
      ['Name', 'e.g. "Professional"'],
      ['Version', 'Auto-incremented on each Publish'],
      ['Tier', 'Optional label (basic / pro / enterprise)'],
      ['Module refs', 'Pinned to specific Module versions'],
      ['Variable Component refs', 'Extras on top of what the modules bring'],
      ['Feature Flags', 'Bundle-level flags'],
      ['Available periods', 'Monthly / Quarterly / Yearly'],
    ],
    example: {
      title: 'Professional Bundle · v2',
      lines: [
        ['Modules', 'Analytics v3 (Pro), Support v1'],
        ['Extras', 'priority-support flag'],
        ['Status', 'Published'],
      ],
    },
    connects: ['Subscribed to by customers', 'Pins Module versions', 'Pinned by Subscriptions'],
  },

  meter: {
    id: 'meter',
    name: 'Meter',
    cluster: 'catalog',
    oneLiner: 'The definition of something you meter — seats, GB, API calls, GMV.',
    plain:
      "A Meter is the unit you measure — 'seat', 'gb', 'api-call', 'gmv'. It carries no money on its own. To bill on a Meter you attach a Variable Component, which adds a pricing model on top. One Meter can power many components (a 'seats' meter might back a flat-rate seat charge in one bundle and a tiered seat charge in another). You no longer need to create a Meter before creating a component — leave it unset and Revvly provisions one automatically from the component's name; binding to an existing Meter is available as an advanced option when you deliberately want to share usage-tracking identity across components. Meters live in Settings → Meters; the internal field and API parameter names (variableType, variableSlug, variableId) predate the rename and are kept for backward compatibility.",
    fields: [
      ['Meter type', 'Stable key (e.g. "seat", "gb") — internal field name variableType'],
      ['Display name', 'How it appears in the UI and invoices'],
      ['Unit label / plural', 'For invoice line items'],
      ['Reset behaviour', 'Carries forward (default) / Resets each period'],
      ['Billed value', 'Highest observed (default) / Total over the period / Value at period end'],
      [
        'Slug',
        'URL-safe key the usage API matches on — derived from the meter type if you do not set one',
      ],
      ['Status', 'Active / Deprecated'],
    ],
    example: {
      title: 'User seats',
      lines: [
        ['Type', 'seat'],
        ['Unit', '1 seat / 5 seats'],
        ['Measures', 'Carries forward, billed on the highest observed'],
      ],
    },
    connects: ['Attached to by Variable Components', 'Counted via Subscription Variable Usage'],
  },

  variableComponent: {
    id: 'variableComponent',
    name: 'Variable Component',
    cluster: 'pricing',
    oneLiner: 'A versioned, priced rule attached to a Meter — flat, tiered, overage, commission.',
    plain:
      "Variable Components are the core of Revvly's pricing engine. Each one attaches a pricing model — flat rate, volume tiered, graduated tiered, stairstep, true overage, committed overage, commission, or promotional — to a Meter. Two settings decide how it measures: whether the quantity carries forward at the period boundary or resets, and which number in the period is rated. A component whose quantity resets is metered consumption — its total is only final once the period closes, so it bills in arrears and its invoice waits for a sealed usage reading (see Capture metered usage). Its unreported-usage policy says what an empty period means for it: hold the invoice for a human, or seal at zero. A component carries its own operator-facing name (it no longer borrows the bound Meter's display name), and creating one no longer requires creating a Meter first — leave the meter unset and Revvly provisions one automatically from the component's name; binding to an existing Meter stays available as an advanced option. As of v4 a component carries only the structure of the model (tier/step boundaries, included units, committed quantity, proration, billing timing, measurement mode) — the actual money lives in a separate Variable Component Price scoped per currency. Components are versioned: once Published, the structure is frozen, and you publish a new version to change it. Modules and Bundles reference component versions; subscriptions pin them.",
    fields: [
      [
        'Name',
        'Operator-facing label, set directly at creation — no longer inherited from the Meter',
      ],
      [
        'Model type',
        'Flat / Volume tiered / Graduated / Stairstep / True overage / Committed overage / Commission / Promotional',
      ],
      [
        'Meter',
        'The metric this prices — optional at creation, auto-provisioned from the name if left unset',
      ],
      [
        'Structure',
        'Tier/step boundaries, included units, committed quantity — but no money (that lives on Variable Component Price)',
      ],
      ['Version', 'Auto-incremented on each Publish'],
      ['Status', 'Draft / Published / Archived'],
      ['Proration', 'Full month / Daily / None'],
      ['Billing timing', 'Advance / Arrears — a per-period quantity must bill in arrears'],
      [
        'Reset behaviour',
        'Carries forward / Resets each period (this is what gates the invoice on a sealed reading)',
      ],
      ['Billed value', 'Highest observed / Total over the period / Value at period end'],
      [
        'Unreported usage policy',
        'Review — hold the invoice for a human (default) / Zero — seal an empty period at zero',
      ],
      ['Limit period', 'How included quantities reset (rolling, calendar, billing-cycle)'],
    ],
    example: {
      title: 'Seats — flat rate · v2',
      lines: [
        ['Model', 'Flat rate, $5 / seat / month'],
        ['Included in Pro tier', '10 seats free'],
        ['Status', 'Published'],
      ],
    },
    connects: [
      'Attaches to a Meter',
      'Referenced by Modules and Bundles via variableComponentRefs (pinned to a version)',
      'Priced per scope via Variable Component Price',
      'Overrideable per customer via Custom Pricing Agreement',
    ],
  },

  variableComponentPrice: {
    id: 'variableComponentPrice',
    name: 'Variable Component Price',
    cluster: 'pricing',
    oneLiner: 'A scoped, multi-currency price for a Variable Component.',
    plain:
      "A Variable Component sets the shape of pricing (flat, tiered, …). A Variable Component Price sets the numbers at a particular scope. Scopes are layered: a Global default, optionally overridden at a Module or Bundle scope, optionally overridden again at a single Subscription scope. At billing time Revvly walks the scopes from most-specific to least-specific and picks the first matching currency. Each scope can be further qualified by region (a per-market price) and by billing term (per-period overrides). This is what lets you ship 'enterprise rates' at the bundle scope, or one-off deals at the subscription scope, without touching the published component. A more specific scope replaces the price it beats rather than merging into it, so every row must carry the monetary fields the component's model bills on — one rate per tier for a volume or graduated tiered component, one flat fee per step for a stairstep one, both halves of the committed and overage pair. A mis-shaped row is rejected when you save it, at every scope, because it could never produce a charge.",
    fields: [
      ['Component', 'Which Variable Component this prices'],
      ['Component version', 'Pinned to a specific published version'],
      ['Scope', 'Subscription / Bundle / Module / Global default'],
      ['Scope id + version', 'Which bundle/module/subscription it applies to'],
      ['Region', 'Optional — a per-market price that wins over the region-agnostic row'],
      [
        'Prices by currency',
        'One row per supported currency, each carrying the fields its pricing model needs',
      ],
      [
        'Per-period overrides',
        'Optional — term-specific prices (e.g. a cheaper yearly rate) that win over the period-agnostic row',
      ],
      ['Status', 'Draft / Published / Archived'],
    ],
    example: {
      title: 'Pro Bundle override — Seats',
      lines: [
        ['Scope', 'Bundle: Professional v2'],
        ['Prices', '$4 USD, €3.80 EUR'],
        ['Status', 'Published'],
      ],
    },
    connects: [
      'Prices a specific Variable Component version',
      'Resolved at billing time by walking Subscription → Bundle → Module → Global',
      'Custom Pricing Agreements override even the subscription scope',
    ],
  },

  customPricingAgreement: {
    id: 'customPricingAgreement',
    name: 'Custom Pricing Agreement',
    cluster: 'pricing',
    oneLiner: 'A negotiated per-customer override for a single Variable Component.',
    plain:
      "When you negotiate a private deal with one customer — discounted seats, custom commission rate, bespoke tier breakpoints — you write a Custom Pricing Agreement. It targets one customer and one Variable Component, declares which pricing axes it overrides (unit price, tiers, included units, proration, billing timing, currency, …), and takes effect at that customer's next renewal once Published. Drafts are editable; Published agreements are immutable until you Revoke or let them expire.",
    fields: [
      ['Customer', 'The end customer (Organization)'],
      ['Component', 'The Variable Component being overridden'],
      ['Override scope', 'Which axes are active: unit price, tiers, …'],
      ['Effective from', 'Earliest date this can take effect'],
      ['Expires at', 'Optional — sweeps to Archived after this date'],
      ['Status', 'Draft / Published / Archived'],
      ['Override fields', 'Only the axes you opted into are required'],
    ],
    example: {
      title: 'Acme · custom seats',
      lines: [
        ['Override', 'Unit price'],
        ['Value', '$3 / seat (vs $5 list)'],
        ['Effective', '2026-06-01 renewal'],
      ],
    },
    connects: [
      'Targets one Customer and one Variable Component',
      "Effective at the customer's next renewal",
      'Beats Variable Component Price at every scope',
    ],
  },

  discount: {
    id: 'discount',
    name: 'Discount',
    cluster: 'pricing',
    oneLiner: 'A bounded, reversible price reduction — reusable template or one-off.',
    plain:
      "A Discount reduces what a customer pays without touching the catalog or replacing the rate. You can build a reusable Discount Definition (a template shared across customers) or define a one-off inline on a single subscription. Each applied discount is an instance targeting the subscription's base price, one Variable Component, or the whole invoice, as a percentage or a fixed amount. Duration is explicit — first invoice only, a fixed number of renewals, or forever — with an optional calendar end date, and a component discount can be limited to some of the billed units — and when several discounts are active on one subscription, a per-subscription stacking mode decides how they combine. Discounts apply after the normal pricing chain (scopes, agreements, overrides) resolves, can be previewed against the next invoice before saving, and every change lands in an audited Discount History. If you need an unbounded, outright replacement of the rate instead, that's a price override — not a discount.",
    fields: [
      ['Definition vs. instance', 'Reusable template in the library, or a one-off defined inline'],
      ['Type', 'Percentage (currency-agnostic) or fixed amount (per-currency)'],
      ['Target', 'Base price, one Variable Component, or the whole invoice'],
      ['Duration', 'First invoice only / Fixed number of renewals / Forever'],
      ['Stacking mode', 'Per subscription: Combine / Best wins / First wins / Latest wins'],
      ['Status', 'Active / Stopped / Archived (definitions); instances cancel individually'],
    ],
    example: {
      title: 'Launch promo · 20% off',
      lines: [
        ['Target', 'Base price'],
        ['Duration', '3 renewals'],
        ['Stacking', 'Best wins'],
      ],
    },
    connects: [
      'Applied to one Subscription as a Discount Instance',
      'Reduces the base price, one Variable Component, or the whole invoice',
      'Applies after scoped prices, agreements, and overrides resolve',
      'Fully audited via Discount History',
    ],
  },

  apiKey: {
    id: 'apiKey',
    name: 'API Key',
    cluster: 'pricing',
    oneLiner: 'A scoped credential pair for usage ingestion and read access.',
    plain:
      'Programmatic access to Revvly uses API Keys: a Bearer token plus a signing secret. When you issue a key the plaintext is shown once — capture it then or rotate. Keys can be scoped to specific subscriptions or meters, carry READ or WRITE capabilities (plus narrower ones for Support tokens, Forms and knowledge sync), and have their own rate limits: 10 requests per second and 100 per minute by default, adjustable up to 500 per second. Rotating a key issues a successor with a 30-day grace period on the old one; revoking is immediate.',
    fields: [
      ['Name', 'Human-friendly label'],
      ['Scope', 'Subscriptions, meters, read/write'],
      ['Rate limits', 'Per-key throttling'],
      ['Bearer token', 'Plaintext shown once at issuance'],
      ['Signing secret', 'Plaintext shown once at issuance'],
      ['Status', 'Active / Rotated / Revoked'],
    ],
    example: {
      title: 'usage-ingest · prod',
      lines: [
        ['Scope', 'subscription:663b… · meter:api-calls'],
        ['Rate limit', '10 req / sec · 100 req / min (default)'],
        ['Issued', '2026-05-01'],
      ],
    },
    connects: [
      'Authorises the Event Ingestion API',
      'Authorises the invoice payment webhook',
      'Authorises the MCP server (POST /mcp)',
      'Issued and rotated per tenant',
    ],
  },

  client: {
    id: 'client',
    name: 'Client',
    cluster: 'customers',
    oneLiner: "Your customer — the company you're selling to.",
    plain:
      "A Client is a subscription customer — the company you're billing. Every Client belongs to one Region, which sets their default currency. They can hold multiple active Subscriptions, contracts, contacts, and a hybrid feature-flag profile (flags derived from the bundle/module versions their subscription pins, plus any manual overrides). Where the CRM is enabled, a Client is the final stage of a relationship funnel — Company, then a Lead you're in conversation with, then Client — and carries a lifecycle stage, an account owner, and the source it came from. A record only becomes a Client by converting a won Lead; it can never be set to Client by hand and never regresses to an earlier stage. The Clients list and every revenue metric show Client-stage organizations only, so prospects and open leads never inflate your customer count or MRR.",
    fields: [
      ['Name + contact email', ''],
      ['Region', 'Which market they belong to'],
      ['Billing address + tax ID', ''],
      ['Payment terms', 'Days to pay (default 30)'],
      ['Invoice prefix', 'Optional per-customer override'],
      ['Feature flag overrides', 'Manual enable/disable on top of the bundle/module derivation'],
      ['Lifecycle stage', 'CRM funnel stage — Client for a billing customer (CRM add-on)'],
      ['Account owner', 'The rep who owns the relationship (CRM add-on)'],
      ['Source', 'How the relationship began — manual, referral, import… (CRM add-on)'],
    ],
    example: {
      title: 'Acme Corp',
      lines: [
        ['Region', 'US'],
        ['Payment terms', 'Net 30'],
        ['Contact', 'billing@acme.com'],
      ],
    },
    connects: [
      'Belongs to one Region',
      'Has many Subscriptions, Invoices, Contacts',
      'Target of Custom Pricing Agreements',
      'Is the Client stage of the CRM funnel (Company → Lead → Client)',
    ],
  },

  lead: {
    id: 'lead',
    name: 'Lead',
    cluster: 'crm',
    oneLiner: 'A deal in flight — one opportunity moving through a pipeline toward won or lost.',
    plain:
      "A Lead is a piece of business you're working — a trial that might convert, an inbound enquiry, a renewal you're chasing. It sits on a Pipeline at one Stage, carries an estimated value, expected close date, and an owner. Its status — Open, Won, or Lost — is driven by the kind of stage it's in, not set by hand: drag a lead into a Won-kind stage and it's Won. A Lead can point at an existing organization or spin up a brand-new Company in the same step. Only a Won lead can be converted, and converting it flips its organization to a Client (optionally linking the subscription that closed the deal). Leads the Revvly Assistant raises are flagged as AI-assisted and keep the rationale for why they were created.",
    fields: [
      ['Title', 'What the deal is'],
      ['Organization', 'An existing org, or a new Company created inline'],
      ['Primary contact', 'The person you deal with'],
      ['Pipeline + stage', 'Where it sits; the stage kind drives status'],
      ['Status', 'Open / Won / Lost (derived from the stage kind, not set directly)'],
      ['Estimated value + currency', 'Deal size, for the pipeline view'],
      ['Expected close date', 'When you expect it to land'],
      ['Owner', 'The rep working it'],
      ['Source', 'Manual / API / AI signal / Inbound email / Import / Referral'],
      ['Converted subscription', 'Set when a Won lead becomes a Client'],
    ],
    example: {
      title: 'Acme — Pro plan',
      lines: [
        ['Stage', 'Proposal (Open)'],
        ['Value', '$12,000 / yr'],
        ['Owner', 'Dana R.'],
      ],
    },
    connects: [
      'Sits on one Pipeline at one Stage',
      'Points at one Organization (Client or Company)',
      'Converting a Won lead turns its Organization into a Client',
      'Carries Notes, Documents, and Emails on its activity timeline',
    ],
  },

  pipeline: {
    id: 'pipeline',
    name: 'Pipeline',
    cluster: 'crm',
    oneLiner: 'The ordered set of stages a lead moves through — your sales process, as a board.',
    plain:
      "A Pipeline is your sales process written down as an ordered list of Stages. Every CRM workspace starts with a default 'Sales Pipeline' — New → Qualified → Proposal → Won / Lost — which you can rename, recolor, reorder, or extend. Each stage has a kind (Open, Won, or Lost) that decides what a lead landing there becomes; the display name is yours, the kind is what drives status. You can archive a stage and move its leads elsewhere without losing history, and a workspace can run more than one pipeline with one marked as the default new leads land on. Viewed as a board, each stage is a column of lead cards you drag between.",
    fields: [
      ['Name', 'e.g. "Sales Pipeline"'],
      ['Default', 'The pipeline new leads land on'],
      ['Stages', 'Ordered list, each with a name, color, and kind'],
      ['Stage kind', 'Open / Won / Lost — drives each lead’s status'],
      ['Archiving', 'Retire a stage and move its leads to another'],
    ],
    example: {
      title: 'Sales Pipeline',
      lines: [
        ['Stages', 'New · Qualified · Proposal · Won · Lost'],
        ['Default', 'Yes'],
      ],
    },
    connects: [
      'Holds many Leads across its Stages',
      'Rendered as a drag-and-drop board',
      'Stage kind (Won / Lost) sets each Lead’s status',
    ],
  },

  contact: {
    id: 'contact',
    name: 'Contact',
    cluster: 'crm',
    oneLiner: 'A person at a customer or company — the human you actually talk to.',
    plain:
      "A Contact is an individual at an organization — a name, email, phone, and labels. Contacts hang off a Client or a Company, and one is marked primary. Each contact carries its own relationship stage independent of the organization's, so a contact can be Disqualified at a company that is otherwise a Client. Beyond the basics they hold an owner, a source, secondary email addresses, and a marketing-email consent state — Subscribed, Unsubscribed, or Bounced — so you always know who you're allowed to email. Contact email addresses are unique across your workspace: adding one that already exists surfaces the existing contact rather than creating a duplicate. A spreadsheet import creates or matches companies and lands people on the right record in one pass; on a client, the Contacts tab also suggests people at the same domain — unassigned contacts, and addresses that appear in email traffic — so you can add the rest of a company's people without typing their addresses. Notes, documents, and logged emails can all be attached to a specific contact.",
    fields: [
      ['Name + email', 'Plus optional phone and secondary emails'],
      ['Organization', 'The company they belong to'],
      ['Primary', 'One primary contact per organization'],
      [
        'Lifecycle stage',
        'Prospect / In conversation / Client / Disqualified — independent of the org',
      ],
      ['Email consent', 'Subscribed / Unsubscribed / Bounced'],
      ['Owner', 'The rep who owns the relationship'],
      ['Source', 'How the contact entered the system'],
      ['Labels', 'Free-form tags'],
    ],
    example: {
      title: 'Dana Rivera · Acme',
      lines: [
        ['Role', 'Primary contact'],
        ['Consent', 'Subscribed'],
        ['Stage', 'Client'],
      ],
    },
    connects: [
      'Belongs to one Organization (Client or Company)',
      'Can be the primary contact on a Lead',
      'Can be imported from a spreadsheet alongside companies',
      'Suggested from the same email domain on a client Contacts tab',
      'Carries its own Notes, Documents, and Emails',
    ],
  },

  crmActivity: {
    id: 'crmActivity',
    name: 'Activity timeline',
    cluster: 'crm',
    oneLiner:
      'Notes, documents, emails, meetings, and tasks — the running history on any client, contact, or lead.',
    plain:
      'Everything you record against a relationship lands on one timeline. Notes are free text and can be pinned. Documents are files — contracts, proposals, decks — uploaded and downloaded through short-lived signed links, each tagged with a category. Emails are captured by the inbox integration or logged by hand, inbound or outbound, with the body and when it happened; synced mailbox threads also appear on Conversation beside the mail you sent from Revvly, gated on the CRM flag. Looking at a sent email in Conversation does not count as the recipient opening it. Meetings record that you met: the time, who was there, where, and what came of it — logged by hand, or arriving from Calendly when a booking is made. Tasks are the to-dos you and your team work, and can be pinned to a due date that follows a CRM moment such as a lead changing stage or a contract expiring. The timeline merges all of them into a single, paginated feed ordered by when each thing happened — so ‘catch me up on Acme’ has one place to look, with meetings still to come shown separately above it rather than buried in history. Every entry records whether a person, the Revvly Assistant, or the API created it.',
    fields: [
      ['Subject', 'What it is attached to: an Organization, a Contact, or a Lead'],
      ['Notes', 'Free text, optionally pinned'],
      ['Documents', 'Files with a category: Contract / Proposal / Deck / Other'],
      [
        'Emails',
        'Direction, subject, body, and when it happened — including synced mailbox threads on Conversation',
      ],
      [
        'Meetings',
        'When, who attended, where, and the outcome: scheduled, held, no-show, or cancelled',
      ],
      ['Tasks', 'To-dos with status, priority, and event-anchored due dates'],
      ['Created via', 'Direct / Assistant / API'],
    ],
    example: {
      title: "Acme's timeline",
      lines: [
        ['Latest', 'Email · sent proposal'],
        ['Pinned note', 'Renewal owner is Dana'],
        ['Documents', '2 (1 contract)'],
      ],
    },
    connects: [
      'Attaches to a Client, Contact, or Lead',
      'Merges notes, documents, emails (including synced mailbox threads), meetings, and tasks into one feed',
      'Readable and writable by the Revvly Assistant',
    ],
  },

  subscription: {
    id: 'subscription',
    name: 'Subscription',
    cluster: 'customers',
    oneLiner: 'The agreement — one customer, one plan, on a cadence, pinned to specific versions.',
    plain:
      'A Subscription pins exactly one of {a Bundle version, a Module version, or neither} plus a list of pinned Variable Component versions. It freezes its base price at creation; renewals and invoices read that frozen number rather than re-resolving pricing rules. Mid-cycle changes become Expansions. Upgrading a pin is an explicit action — there is no implicit propagation of new module or bundle versions. A Subscription can also carry a billing schedule, which changes when it is invoiced without changing what it costs: a prepaid term bills several periods of the plan, and of the components billed ahead with it, on one invoice at signing, and a deferred activation is signed now and switched on later, sitting Pending — out of MRR, renewals, and usage — until it goes live.',
    fields: [
      ['Client', 'Who'],
      ['Pinned composition', 'Bundle+version OR Module+version OR neither'],
      ['Variable Component refs', 'Pinned component versions'],
      ['Base price + currency', 'Frozen at creation'],
      ['Original amount + FX rate', 'Preserved for imported / converted subscriptions'],
      ['Period', 'Monthly / Quarterly / Yearly'],
      ['Status', 'Trial / Pending / Active / Cancelled / Grace period…'],
      [
        'Billing schedule',
        'Optional — a prepaid term or a deferred go-live, separate from the price',
      ],
      [
        'Subscription type',
        'Standard / Trial / Custom (also Acquisition / Renewal / Expansion in some flows)',
      ],
      ['Renewal info', 'Next renewal date, grace period, last auto-renewal result'],
      ['Pending migration', 'A scheduled bundle/module-version change'],
      [
        'Discounts & overrides',
        'Active Discount Instances, plus any base-price / component-rate / billing-timing overrides',
      ],
    ],
    example: {
      title: 'Acme · Pro v2 · Monthly',
      lines: [
        ['Pinned', 'Bundle Professional v2'],
        ['Price', '$49 USD'],
        ['Status', 'Active · Next renewal May 23'],
      ],
    },
    connects: [
      'Belongs to one Client',
      'Pins exactly one of Bundle / Module / neither',
      'Pins specific Variable Component versions',
      'Generates Invoices, emits Lifecycle Events',
      'Tracks usage via Subscription Variable Usage',
      'Can have Discounts applied (base price, components, or the whole invoice)',
    ],
  },

  subscriptionUsage: {
    id: 'subscriptionUsage',
    name: 'Subscription Variable Usage',
    cluster: 'customers',
    oneLiner: 'The live counter for one meter on one subscription.',
    plain:
      "Whenever a Variable Component is part of a Subscription, Revvly maintains a counter row that tracks current and cumulative usage, peak values, rollover units, and (for commission components) GMV / transaction counts. The counters drive overage charges, true-up invoices, and limit enforcement. Counters reset according to the Variable Component's limit period configuration. For a component whose quantity resets each period, the counter is not the last word: when the period closes Revvly captures the accumulated total as a Usage Reading before the counter resets, and the invoice for that period waits until the reading is sealed or waived. That is what stops a metered period being billed on a number nobody looked at, and it is why an integration that goes quiet holds an invoice rather than quietly billing zero — see the Capture metered usage guide.",
    fields: [
      ['Subscription + Meter', 'What this counter belongs to'],
      ['Reset behaviour', 'Carries forward / Resets each period'],
      ['Billed value', 'Highest observed / Total over the period / Value at period end'],
      ['Capture mode', 'API (an integration increments it) / Manual (an operator enters it)'],
      ['Current / cumulative', 'Live counters'],
      ['Peak count + reached at', 'For peak-mode metrics'],
      ['Rollover units', 'Unspent quota carried into next period'],
      ['Period start / end', 'Billing-period bounds'],
      ['Commission counters', 'GMV, transaction count, refunded amount (commission components)'],
    ],
    example: {
      title: 'Acme · seats',
      lines: [
        ['Current', '15 seats'],
        ['Peak this period', '17 seats'],
        ['Mode', 'Peak'],
      ],
    },
    connects: [
      'One per (Subscription, Meter) pair',
      'Updated by the Event Ingestion API',
      'Closes into a Usage Reading each period on components that reset',
      'Drives Variable Invoice Lines',
    ],
  },

  expansion: {
    id: 'expansion',
    name: 'Expansion',
    cluster: 'customers',
    oneLiner: 'A mid-cycle change — added module, upgraded bundle, raised commitment.',
    plain:
      'When a customer changes their subscription between renewals — adds a module, upgrades to a higher bundle version, raises a committed quantity — Revvly records it as an Expansion with a prorated charge or credit. Each Expansion contributes to Expansion MRR, the measure of upsell revenue from existing customers.',
    fields: [
      ['Subscription', 'Which subscription changed'],
      ['Expansion type', 'Bundle change / Module add / Flag add / Commit raise…'],
      ['Item + quantity change', 'What and how much'],
      ['Prorated amount', 'Charge or credit for the remainder of the cycle'],
      ['Effective date', 'When the change takes effect'],
    ],
    example: {
      title: 'Acme · upgrade Pro v1 → v2',
      lines: [
        ['Prorated charge', '$18.33'],
        ['Effective', 'Apr 23, 2026'],
      ],
    },
    connects: ['Belongs to one Subscription', 'Emits an EXPANDED or CONTRACTED Lifecycle Event'],
  },

  contract: {
    id: 'contract',
    name: 'Contract',
    cluster: 'customers',
    oneLiner: 'The agreement a subscription is sold under, and the committed term it carries.',
    plain:
      "A Contract records an agreement with a client — an SOW, an MSA, an amendment — at any stage from draft, through out for signature, to signed, together with the commercial terms that matter to billing: when the committed term starts, how many months it runs, what happens when it ends, and what leaving early costs. Its whole job is the commitment boundary. Price, cadence and invoices stay with the Subscription, and attaching a contract never moves a subscription's service or invoice dates. The field the entity exists for is the term length, because term length is not billing cadence: an annual commitment paid monthly is a twelve-month term on a monthly subscription. A contract with no term is evergreen, which is the honest shape for an MSA. One contract may cover several subscriptions; a subscription takes its committed term from exactly one, so the boundary always has a single source. When the boundary arrives, the contract's renewal terms decide: an auto-renewing commitment rolls into its next term (the signing date never moves — the term count goes up and the end is re-derived from it), one that ends does not renew past the boundary, and one that renews unless notice is given raises a single item in the renewal approval queue as soon as the notice window opens and blocks the boundary until it is decided. A contract narrows a renewal rather than forcing one.",
    fields: [
      ['Name + type', 'SOW / MSA / Amendment / NDA / Other'],
      ['Status', 'Draft / Out for signature / Signed / Cancelled / Expired'],
      ['Signed on', 'When the document was signed — distinct from when the term starts'],
      ['Term starts', 'What the committed term is anchored to'],
      ['Term (months)', 'Null means evergreen — no committed span'],
      ['Term ends', 'Derived: term start + months × (terms elapsed + 1)'],
      [
        'Terms elapsed',
        'Completed committed terms — 0 until an auto-renewing commitment first rolls',
      ],
      [
        'At end of term',
        'Ends / Renews automatically / Renews unless notice is given (+ notice days)',
      ],
      [
        'Early termination policy',
        'Charge the remainder / Keep billing to the committed end / Waive — a default for a cancellation, not the answer',
      ],
      ['Subscriptions', 'The subscriptions this contract covers'],
      ['File', 'The signed document itself, when uploaded'],
    ],
    example: {
      title: 'Acme SOW 2026-01',
      lines: [
        ['Term', '12 months from Mar 1, 2026'],
        ['Cadence', 'Monthly subscription — 12 invoices, one commitment'],
        ['At end of term', 'Renews unless notice is given (60 days)'],
        ['If they leave early', 'Keep billing to the committed end'],
      ],
    },
    connects: [
      'Belongs to one Client',
      'Covers one or more Subscriptions, each taking its committed term from exactly one contract',
      'Consulted by the renewal boundary when a period end reaches the committed end',
      'Supplies the default early-termination policy a cancellation inherits',
    ],
  },

  invoice: {
    id: 'invoice',
    name: 'Invoice',
    cluster: 'billing',
    oneLiner: 'A billing document issued to a customer.',
    plain:
      "Invoices are the billing documents your customers pay. Revvly auto-generates a subscription invoice on each renewal cycle, and for mid-cycle expansions, service charges, refunds, and variable usage true-ups. Renewal invoices can also be issued ahead of the renewal date by a per-period lead time you configure. Each invoice contains line items — '10 seats @ $5', 'Pro Bundle April 2026' — and tracks payment status through to collection. A plan made of several priced modules bills one line per module, named after it, rather than one combined line; the lines are still a single discount target, so a fixed amount off the plan comes off once and is spread across them. An invoice also has an issue state, separate from payment: a renewal that cannot be totalled yet — because a metered period has not sealed — is created as a Draft with no number, no dates and no payment status, so it stays out of receivables and AR aging until it issues. It picks up its number and dates at the moment it issues, which is what keeps numbers in issue order. A draft that is abandoned is voided, never deleted. A Pending or Overdue subscription invoice can be regenerated to pick up the subscription's current pricing and the client's current payment terms; settled invoices are left untouched. Regenerating a Pending or Overdue invoice that carries tax also re-checks the tax over its existing lines, so a correction to the tax setup (a client taxed in a country you are not registered in, a changed rate) reaches an invoice already issued; the tax change is recorded as a reversal and a new entry in the tax ledger rather than an overwrite, and an invoice that is part-paid, a credit note or already credited is left as it is, where a correction goes through a credit note. Unmatched invoices (e.g. from CSV import) can be linked back to a subscription after the fact. A Drive import can also reconstruct the subscriptions from a client's own invoices: the charges are clustered into the plan shapes they imply, catalog gaps can be filled from those charges, and committing a client creates both the invoices and the subscriptions rather than leaving you to invent the relationship first. Payments can be recorded in the dashboard or posted straight from your payment system through the invoice payment webhook, in full or a part at a time — a part-paid invoice tracks how much has been collected and how much is still due, and stays open until the balance clears. A credit note is an invoice too: same collection, same number sequence, same PDF, with a negative total and the type Refund. That is what lets an unapplied credit reduce what a client owes without any receivables query needing to know credit notes exist. A credit note is never chased — no due date, no aging, no dunning — and is either applied to one of the client's open invoices or refunded in cash.",
    fields: [
      [
        'Invoice number',
        'A per-client prefix and one running sequence, e.g. ACME-00042 — stamped when the invoice issues',
      ],
      ['Client + Subscription', 'Who owes, for what'],
      ['Amount + currency', ''],
      ['Dates', 'Invoice date, due date, estimated payment date'],
      ['Issue state', 'Draft (held, not yet issued — no number or dates) / Issued / Void'],
      ['Held reason', 'Why a draft is waiting — typically a metered period that has not sealed'],
      [
        'Payment status',
        'Paid / Pending / Overdue / Failed — null on a draft, since nothing is owed yet',
      ],
      ['Amount paid + amount due', 'What has been collected, and what is still outstanding'],
      ['Type', 'Subscription / Renewal / Expansion / Service / Refund (a credit note)'],
      [
        'Credits invoice',
        'On a credit note: the invoice it was raised against, and the ceiling it cannot exceed',
      ],
      [
        'Credit applied + remaining',
        'On a credit note: how much has been spent on receivables, and what is left to spend or refund',
      ],
      ['Line items (embedded)', 'Description, qty, unit price, total'],
      ['Attachments', 'Contracts, POs'],
      ['Source', 'System / Excel / Google Drive'],
      ['Is unmatched', 'True for imports not yet linked to a subscription'],
    ],
    example: {
      title: 'ACME-00042',
      lines: [
        ['Amount', '$67.33 USD'],
        ['Status', 'Issued · Paid'],
        ['Due', 'May 23, 2026'],
      ],
    },
    connects: [
      'Belongs to one Client',
      'Optionally linked to a Subscription',
      'Imported Drive invoices can reconstruct the subscriptions they imply',
      'Generated from Billing Period Snapshots for variable usage',
      'Held as a Draft until every Usage Reading it carries is sealed or waived',
      'Applies active Discounts as the final step (post-pricing resolution)',
      'May carry credit notes raised against it, up to its own total',
    ],
  },

  lifecycleEvent: {
    id: 'lifecycleEvent',
    name: 'Lifecycle Event',
    cluster: 'billing',
    oneLiner: 'An immutable record of every revenue-moving moment.',
    plain:
      "Every time something revenue-moving happens — a subscription is created, renewed, expanded, contracted, cancelled, or churned — Revvly writes a Lifecycle Event. These events are the source of truth for MRR, ARR, NRR, and churn analytics; subscription status alone is not used for those metrics. They're append-only: corrections go in as new events, never as overwrites. Each event stamps both the native-currency delta and the reporting-currency delta, so FX shifts don't rewrite history.",
    fields: [
      [
        'Type',
        'Created / Renewed / Reactivated / Expanded / Contracted / Cancel-scheduled / Churned',
      ],
      ['MRR delta (reporting)', 'Signed, in your reporting currency'],
      ['Native MRR + currency', 'Preserved for historical re-conversion'],
      ['Churn reason', 'Required when type = Churned'],
      ['Occurred at', 'The economically real moment'],
      ['Created at', 'When it was written to the log'],
    ],
    example: {
      title: 'EXPANDED · Acme · +$25 MRR',
      lines: [
        ['Occurred', 'Apr 23, 2026'],
        ['Native', '+$25 USD'],
        ['Reporting', '+$25 USD'],
      ],
    },
    connects: [
      'One Subscription, one Client',
      'Never updated or deleted — corrections go in as new events',
      'Powers all MRR / ARR / NRR / churn analytics',
    ],
  },

  exchangeRate: {
    id: 'exchangeRate',
    name: 'Exchange Rate',
    cluster: 'catalog',
    oneLiner: 'Daily currency rates, so foreign subscriptions roll up to your reporting currency.',
    plain:
      'If your reporting currency is USD but you have customers paying in EUR, GBP, or other currencies, Revvly uses daily exchange rates to convert all subscription revenue into a single reporting rollup. Rates are shared across all workspaces and fetched daily; there are no per-workspace rate overrides. The rate applied is stored on each Subscription and each Lifecycle Event for historical accuracy and revenue reconciliation.',
    fields: [
      ['Base currency', ''],
      ['Effective date', 'One set of rates per day'],
      ['Rates', 'Map of currency → rate'],
      ['Source', 'Fixer (daily fetch)'],
    ],
    example: {
      title: 'USD · 2026-04-23',
      lines: [
        ['EUR', '0.924'],
        ['GBP', '0.794'],
        ['Source', 'Fixer'],
      ],
    },
    connects: [
      'Subscriptions store the rate they used for audit',
      'Lifecycle Events stamp both native and reporting deltas',
    ],
  },

  pricingRule: {
    id: 'pricingRule',
    name: 'Pricing Rule',
    cluster: 'pricing',
    oneLiner: 'The base price of a Bundle or Module, per region and period.',
    plain:
      'Pricing Rules set the base price. They carry a single price for a Bundle, a Module, or a Feature Flag at one region × period combination, and they drive the pricing calculator that prices every new subscription. Usage-based charges sit on top: Variable Components and Variable Component Prices cover tiered, overage, commission, and per-scope pricing for the Meters you bill on.',
    fields: [
      ['What it prices', 'A Module, a Bundle, or a Feature Flag'],
      ['Price + currency', 'e.g. $49 USD'],
      ['Scope', 'Global, regional, or customer-specific override'],
      ['Period', 'Monthly / Quarterly / Yearly'],
      ['Valid from / to', 'Lets you schedule price changes'],
      ['Variable limits', 'Included quantities + overage rates (older rules)'],
    ],
    example: {
      title: 'Pro Bundle · US · Monthly',
      lines: [
        ['Price', '$49.00 USD'],
        ['Valid from', '2026-01-01'],
      ],
    },
    connects: [
      'Used by the pricing calculator for the base price',
      'Usage-based charges are priced separately by Variable Component + Variable Component Price',
    ],
  },
};

export const CLUSTER_LABELS: Record<ClusterKey, string> = {
  root: 'Foundation',
  catalog: 'Catalog',
  pricing: 'Pricing engine',
  customers: 'Customers',
  crm: 'CRM',
  billing: 'Billing & analytics',
};

export type NavItem =
  | { type: 'link'; id: string; label: string; path: string }
  | { type: 'group'; label: string; ids: string[] };

export const NAV: NavItem[] = [
  { type: 'link', id: 'home', label: 'Overview', path: '' },
  { type: 'link', id: 'quickstart', label: 'Quickstart', path: 'quickstart' },
  { type: 'link', id: 'example', label: 'Worked example', path: 'example' },
  { type: 'link', id: 'guides', label: 'Guides', path: 'guides' },
  { type: 'link', id: 'reference', label: 'Reference', path: 'reference' },
  { type: 'link', id: 'assistant', label: 'Assistant', path: 'assistant' },
  { type: 'link', id: 'changelog', label: 'Changelog', path: 'changelog' },
  { type: 'group', label: 'Foundation', ids: ['region', 'exchangeRate'] },
  { type: 'group', label: 'Catalog', ids: ['featureFlag', 'module', 'bundle', 'meter'] },
  {
    type: 'group',
    label: 'Pricing',
    ids: [
      'variableComponent',
      'variableComponentPrice',
      'customPricingAgreement',
      'discount',
      'apiKey',
      'pricingRule',
    ],
  },
  {
    type: 'group',
    label: 'Customers',
    ids: ['client', 'contract', 'subscription', 'subscriptionUsage', 'expansion'],
  },
  { type: 'group', label: 'CRM', ids: ['lead', 'pipeline', 'contact', 'crmActivity'] },
  { type: 'group', label: 'Billing & analytics', ids: ['invoice', 'lifecycleEvent'] },
];

export interface OnboardingStep {
  label: string;
  detail: string;
  entity: string;
}

export interface OnboardingPhase {
  phase: string;
  title: string;
  subtitle: string;
  steps: OnboardingStep[];
}

export const ONBOARDING: OnboardingPhase[] = [
  {
    phase: 'Phase 1',
    title: 'Set up your catalog',
    subtitle: 'Build your product once. You rarely touch this again.',
    steps: [
      {
        label: 'Configure your Tenant',
        detail: 'Name, reporting currency, invoice prefix, tax setup.',
        entity: 'tenant',
      },
      {
        label: 'Create Regions',
        detail: 'One per market you sell in. Each has a default currency.',
        entity: 'region',
      },
      {
        label: 'Create Feature Flags',
        detail: 'The smallest switches — one per capability you want to toggle.',
        entity: 'featureFlag',
      },
      {
        label: 'Define Meters',
        detail:
          'Anything you meter: seats, storage, calls, GMV. Optional up front — components can provision one for you.',
        entity: 'meter',
      },
      {
        label: 'Author Variable Components',
        detail:
          'Name the component and choose a pricing model: flat, tiered, overage, commission, … No need to create a Meter first.',
        entity: 'variableComponent',
      },
      {
        label: 'Set Variable Component Prices',
        detail: 'Publish the numbers per currency, optionally overridden at module/bundle scope.',
        entity: 'variableComponentPrice',
      },
      {
        label: 'Compose Modules',
        detail:
          'Pin Variable Component versions and Feature Flags into capability groups with tiers.',
        entity: 'module',
      },
      {
        label: 'Compose Bundles',
        detail: 'Pin Module versions into the plans customers buy: Starter, Pro, Enterprise.',
        entity: 'bundle',
      },
    ],
  },
  {
    phase: 'Phase 2',
    title: 'Sell to a customer',
    subtitle: 'Now your catalog is purchasable.',
    steps: [
      {
        label: 'Create a Client',
        detail: 'Your customer. Assign them to a Region.',
        entity: 'client',
      },
      {
        label: 'Create a Subscription',
        detail: 'Pin a Bundle version (or Module version) plus any extra Variable Components.',
        entity: 'subscription',
      },
      {
        label: 'Optionally negotiate a private deal',
        detail:
          'A Custom Pricing Agreement overrides one component for one customer at next renewal.',
        entity: 'customPricingAgreement',
      },
      {
        label: 'Revvly emits a CREATED event',
        detail: 'MRR goes up. Analytics update.',
        entity: 'lifecycleEvent',
      },
    ],
  },
  {
    phase: 'Phase 3',
    title: 'Run the subscription',
    subtitle: 'Things happen. Revvly records them.',
    steps: [
      {
        label: 'Stream usage events',
        detail: 'Counters update on each Subscription Variable Usage row in real time.',
        entity: 'subscriptionUsage',
      },
      {
        label: 'Invoices are issued',
        detail: 'Each renewal cycle, plus variable-usage true-ups and expansion charges.',
        entity: 'invoice',
      },
      {
        label: 'Mid-cycle changes become Expansions',
        detail: 'New modules, version upgrades, commitment raises — prorated automatically.',
        entity: 'expansion',
      },
      {
        label: 'Adjust price with Discounts and overrides',
        detail:
          'Bounded, reversible reductions via Discounts; outright rate replacements via subscription-scoped overrides.',
        entity: 'discount',
      },
      {
        label: 'Renewals and churn happen',
        detail: 'Lifecycle events are written for every moment that moves the needle.',
        entity: 'lifecycleEvent',
      },
      {
        label: 'Analytics rebuild from the event log',
        detail: 'MRR, ARR, NRR, churn — always derived, never manually edited.',
        entity: 'lifecycleEvent',
      },
    ],
  },
];

export interface WorkedStep {
  label: string;
  text: string;
}

export const WORKED: { name: string; subtitle: string; steps: WorkedStep[] } = {
  name: 'Acme Corp',
  subtitle: 'A fictional customer we follow through the whole flow.',
  steps: [
    {
      label: 'The Tenant',
      text: "You — 'Acme SaaS' — run your subscription business on Revvly in USD.",
    },
    {
      label: 'The Region',
      text: "You've set up a 'US' region with USD as its default currency and CAD as a supported alternate.",
    },
    {
      label: 'The Meter',
      text: "You've defined a 'seat' Meter — the quantity carries forward between periods and is billed on the highest value observed, so customers are charged on their peak seat count in the period.",
    },
    {
      label: 'The Variable Component',
      text: "On top of 'seat' you publish a flat-rate Variable Component v1: $5 per seat per month, 10 included for any subscription that bundles it.",
    },
    {
      label: 'The Catalog',
      text: "You compose an 'Analytics Module' (pinning the seats component v1) and a 'Professional Bundle' (pinning Analytics Module v1 plus a 'priority-support' flag).",
    },
    {
      label: 'The Price',
      text: 'The Professional Bundle resolves to $49 USD / month from a Global default Variable Component Price, with seats above 10 priced at the flat rate.',
    },
    { label: 'The Customer', text: 'Acme Corp signs up — a Client in the US region.' },
    {
      label: 'The Subscription',
      text: 'They subscribe to the Professional Bundle v1, monthly, starting April 23. The subscription pins Bundle Professional v1 and Analytics Module v1. A CREATED event fires: +$49 MRR.',
    },
    {
      label: 'The Usage',
      text: 'Two weeks in, Acme reaches 15 active seats. Subscription Variable Usage records peak=15. The next invoice will include 5 × $5 = $25 in overage seats.',
    },
    {
      label: 'The Expansion',
      text: 'Acme upgrades to Bundle Professional v2 (a new published version of the bundle). An Expansion is recorded with a prorated charge of $18.33. An EXPANDED event fires: +$25 MRR.',
    },
    {
      label: 'The Private Deal',
      text: 'Mid-year, you negotiate Acme down to $3 per seat. A Custom Pricing Agreement targets the seats component, overrides the unit price axis, and takes effect at their next renewal.',
    },
    {
      label: 'The Discount',
      text: 'To sweeten the deal further, you apply a 10% discount on the base price for the next three renewals — previewed against the upcoming invoice before saving. It stacks with nothing else, so the Best-wins stacking mode is moot for now.',
    },
    {
      label: 'The Invoice',
      text: 'Next cycle, Revvly issues invoice ACME-00001 for $74 — base plan plus seat overage. Pending → Paid moves the receivable through Finance.',
    },
    {
      label: 'The Renewal',
      text: "Thirty days before the anniversary, Acme appears in the Renewal queue. You approve the renewal action; at the boundary Revvly applies the pending Custom Pricing Agreement, decrements the discount's remaining renewals, issues the renewal invoice, and writes a RENEWED lifecycle event.",
    },
  ],
};
